import axios from 'axios';
import Qs from 'qs';
// import Utils from 'utils/Utils';
import {AES, enc} from 'crypto-js';
import moment from 'moment';
import Conf from 'utils/Conf';
import Utils from 'utils/Utils';

const storageAvailable = Utils.storageAvailable('localStorage');

const IP_LOC_URL = `${Conf.apiBaseUriLegacy}/ip_loc`;

const GOOGLE_API_URL = `https://www.googleapis.com/geolocation/v1/geolocate?key=${Conf.googleGeolocateApiKey}`;

const UNCERTAINTY_SPEED = 1; // in meters per second

// const getReliability = ({ source }) => {
//   switch (source) {
//     case 'google':
//       return 1;
//     case 'browser':
//       return 1;
//     default:
//       return 1;
//   }
// };

const getTimeUncertainty = ({t}) => {
  const now = moment();
  return now.diff(t, 'seconds') * UNCERTAINTY_SPEED;
};

const getWeightedUncertaintyRadius = a => a.accuracy + getTimeUncertainty(a); // / getReliability(a)

const saveGeolocToBackend = async geoloc => {
  if (geoloc.accuracy > Conf.geolocMaxAccuracy) {
    return null;
  }
  const newGeoloc = {
    ...geoloc,
    current_datetime: moment().format('YYYY-MM-DD HH:mm:ss'),
  };
  return await axios.post(IP_LOC_URL, Qs.stringify(newGeoloc), {withCredentials: true});
};

const saveGeolocToLocalStorage = async geoloc => {
  // TODO: check localStorage first, then fallback on something else if not available.
  if (storageAvailable) {
    const ciphertext = AES.encrypt(JSON.stringify(geoloc), Conf.gpsSecret).toString();
    localStorage.setItem('geoloc', ciphertext);
    return geoloc;
  }
};

const getCurrentPosition = options => {
  return new Promise(function (resolve, reject) {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
};

const saveGeoloc = async geoloc => {
  await saveGeolocToLocalStorage(geoloc);
  await saveGeolocToBackend(geoloc);
};

let dataFromBackend = null;
const getDataFromBackend = async () => {
  if (!dataFromBackend) {
    const {data} = await axios.get(IP_LOC_URL, {withCredentials: true});
    dataFromBackend = data;
  }
  return dataFromBackend;
};

const getGeolocFromIp = async () => {
  const {accuracy, latitude, longitude, current_datetime, t} = await getDataFromBackend();
  const geoloc = {
    accuracy,
    latitude,
    longitude,
    // transform the datetime in local time using server local time comparison
    t: moment()
      .subtract(moment.duration(moment(current_datetime).diff(t)))
      .format('YYYY-MM-DD HH:mm:ss'),
    source: 'backend',
  };
  return accuracy ? geoloc : null;
};

const getCountryFromIp = async () => {
  const {pays_id} = await getDataFromBackend();
  return pays_id;
};

const getGeolocFromLocalStorage = async () => {
  // TODO: check localStorage first, then fallback on something else if not available.
  if (storageAvailable) {
    const ciphertext = localStorage.getItem('geoloc');
    if (!ciphertext) return null;

    const bytes = AES.decrypt(ciphertext, Conf.gpsSecret);
    const geoloc = JSON.parse(bytes.toString(enc.Utf8));
    if (!geoloc.accuracy || !geoloc.latitude || !geoloc.longitude) return null;
    return {
      ...geoloc,
      source: 'localStorage',
    };
  } else {
    return null;
  }
};

const getGeolocFromNavigator = async () => {
  // TODO: check if navigator.geolocation is available
  try {
    const position = await getCurrentPosition({timeout: 5000});
    if (position.coords.accuracy >= Conf.geolocMaxAccuracy) {
      // Not enough precise
      return null;
    }
    return {
      accuracy: position.coords.accuracy,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      t: moment().format('YYYY-MM-DD HH:mm:ss'),
      source: 'browserApi',
    };
  } catch (err) {
    return null;
  }
};

const getGeolocFromGoogle = async () => {
  try {
    const result = await axios.post(GOOGLE_API_URL);
    const accuracy = result.data.accuracy;
    if (accuracy >= Conf.geolocMaxAccuracy) {
      // Not enough precise
      return null;
    }
    return {
      accuracy,
      latitude: result.data.location.lat,
      longitude: result.data.location.lng,
      t: moment().format('YYYY-MM-DD HH:mm:ss'),
      source: 'google',
    };
  } catch (err) {
    return null;
  }
};

const chooseBestGeoloc = (geolocs = []) => {
  const validGeolocs = geolocs.filter(Boolean);
  if (validGeolocs.length === 0) return null;
  return validGeolocs.sort((a, b) => getWeightedUncertaintyRadius(a) - getWeightedUncertaintyRadius(b))[0];
};

// EXPOSED FROM OUTSIDE OF THIS MODULE:

// get geolocs from all sources (saved and recomputed) and returns the best one
export const getGeoloc = async () => {
  const [localStorageGeoloc, backendSavedGeoloc, navigatorGeoloc, googleGeoloc] = await Promise.all([
    getGeolocFromLocalStorage(),
    getGeolocFromIp(),
    getGeolocFromNavigator(),
    getGeolocFromGoogle(),
  ]);

  // const localStorageGeoloc = await getGeolocFromLocalStorage();
  // const backendSavedGeoloc = await getGeolocFromIp();
  // const navigatorGeoloc = await getGeolocFromNavigator();
  // const googleGeoloc = await getGeolocFromGoogle();

  const bestGeoloc = chooseBestGeoloc([localStorageGeoloc, backendSavedGeoloc, navigatorGeoloc, googleGeoloc]);

  if (bestGeoloc) {
    saveGeoloc(bestGeoloc);
  }

  return bestGeoloc;
};

export const getCountry = getCountryFromIp;

// get geoloc from recomputed source and returns the best one
export const refreshGeoloc = async () => {
  const navigatorGeoloc = await getGeolocFromNavigator();
  const googleGeoloc = await getGeolocFromGoogle();
  const bestGeoloc = chooseBestGeoloc([navigatorGeoloc, googleGeoloc]);
  if (bestGeoloc) {
    saveGeoloc(bestGeoloc);
  }
  return bestGeoloc;
};
